import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useCallback } from 'react';
import { fetcher } from 'src/api/fetcher';
import { API_RESOURCES, PAGINATION_IGNORE_DEFAULT_LIMIT } from 'src/utils/constants';

export const DATE_LOCALES = {
  US: 'en-EN',
  ISO: 'ISO',
};

export const DATE_FORMATS = {
  [DATE_LOCALES.US]: 'MM/DD/YYYY',
  [DATE_LOCALES.ISO]: 'YYYY/MM/DD',
};

export const DATE_TIME_FORMATS = {
  [DATE_LOCALES.US]: 'MM/DD/YYYY, h:mm A',
  [DATE_LOCALES.ISO]: 'YYYY/MM/DD, HH:mm',
};

export const TIME_FORMATS = {
  [DATE_LOCALES.US]: 'h:mm A',
  [DATE_LOCALES.ISO]: 'HH:mm',
};

/* Friendly month-name tier — for card meta lines (rapidfab shows these via
 * FormattedDate month="short"); the numeric tables above stay the canonical
 * form for labeled values (Start/Finish rows, reports). */
export const FRIENDLY_DATE_TIME_FORMATS = {
  [DATE_LOCALES.US]: 'MMM D, YYYY, h:mm A',
  [DATE_LOCALES.ISO]: 'D MMM YYYY, HH:mm',
};

export const FRIENDLY_SHORT_DATE_TIME_FORMATS = {
  [DATE_LOCALES.US]: 'MMM D, h:mm A',
  [DATE_LOCALES.ISO]: 'D MMM, HH:mm',
};

/**
 * The user's date locale, derived from the bureau's default currency exactly
 * like rapidfab's useDateLocale: USD bureaus read US-style dates
 * (MM/DD/YYYY, h:mm A), every other bureau reads ISO (YYYY/MM/DD, HH:mm).
 * Values render in the browser's local timezone (plain dayjs, no conversion) —
 * rapidfab parity. Falls back to ISO while loading or if the lookup fails.
 */
export const useDateLocale = () => {
  const { data: defaultCurrency } = useQuery({
    queryKey: ['bureau-default-currency'],
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    queryFn: () =>
      fetcher(API_RESOURCES.CURRENCY_CONVERSION, {
        params: { 'page[limit]': PAGINATION_IGNORE_DEFAULT_LIMIT },
      }),
    select: data =>
      (data.resources || []).find(conversion => conversion.is_bureau_default)?.currency || null,
  });

  const locale = defaultCurrency === 'USD' ? DATE_LOCALES.US : DATE_LOCALES.ISO;

  const formattedDate = useCallback(
    value => (value ? dayjs(value).format(DATE_FORMATS[locale]) : ''),
    [locale]
  );
  const formattedDateTime = useCallback(
    value => (value ? dayjs(value).format(DATE_TIME_FORMATS[locale]) : ''),
    [locale]
  );
  const formattedTime = useCallback(
    value => (value ? dayjs(value).format(TIME_FORMATS[locale]) : ''),
    [locale]
  );

  /** Month-name datetime for card meta lines — "Jul 1, 2026, 5:12 PM" / "1 Jul 2026, 17:12". */
  const formattedFriendlyDateTime = useCallback(
    value => (value ? dayjs(value).format(FRIENDLY_DATE_TIME_FORMATS[locale]) : ''),
    [locale]
  );

  /**
   * Compact start-finish window for cards whose leading DateTile already shows
   * the start date — so the text never repeats it, and the finish date speaks
   * the tile's month-name language. Same day: "3:00 PM - 4:00 PM". Crossing
   * days: "12:55 PM - Aug 18, 1:55 PM" (year appears only when the finish
   * year differs), so a July→August block stays one readable line.
   */
  const formattedCompactWindow = useCallback(
    (start, finish) => {
      if (!start) return finish ? formattedFriendlyDateTime(finish) : '';
      if (!finish) return formattedTime(start);
      const from = dayjs(start);
      const to = dayjs(finish);
      if (from.isSame(to, 'day')) return `${formattedTime(start)} - ${formattedTime(finish)}`;
      if (from.isSame(to, 'year')) {
        return `${formattedTime(start)} - ${to.format(FRIENDLY_SHORT_DATE_TIME_FORMATS[locale])}`;
      }
      return `${formattedTime(start)} - ${formattedFriendlyDateTime(finish)}`;
    },
    [locale, formattedFriendlyDateTime, formattedTime]
  );

  return {
    locale,
    dateFormat: DATE_FORMATS[locale],
    dateTimeFormat: DATE_TIME_FORMATS[locale],
    formattedDate,
    formattedDateTime,
    formattedTime,
    formattedFriendlyDateTime,
    formattedCompactWindow,
  };
};
